import configLib from '../../../configLib';
import { SOROrderRequestDataBase } from '../../models/gateway/types';
import { FXOrderRequest, Order } from '../../models/trading/types';
import { sorOrderEvent } from '../../store/common-actions';
import { cancelOrderFailed, modifyOrderFailed, newFXOrderFailed, newOrderFailed } from '../../store/trading';
import { ModifyOrCancelOrderRequest } from '../../store/trading/types';
import OrderInfoCache from '../../store-util/OrderInfoCache';
import { uuidv4 } from '../../util/tools';
import { setSOROrderData } from '../../util/TradingHelpers';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { OrderWebSocketRequest } from '../subscription/types';
import { GatewayWebSocketService } from '../web-socket/GatewayWebSocket.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

const LOG_PREFIX = '[ORDER-WS]';
const logger = LoggerService.getLogger();

export class OrderWebSocketService {
  protected readonly gatewayService: GatewayWebSocketService;

  private static instance: OrderWebSocketService | undefined;

  protected orderRequests: Array<OrderWebSocketRequest | FXOrderRequest> = [];

  protected constructor() {
    this.gatewayService = GatewayWebSocketService.getInstance();
    this.registerResponseHandler();
  }

  public static getInstance(): OrderWebSocketService {
    this.instance ??= new OrderWebSocketService();
    return this.instance;
  }

  protected registerResponseHandler(): void {
    const handler = (event: WebSocketEventData<any>) => this.handleResponseEvent(event);

    // Subscribe to multiple order-related responses
    this.gatewayService.subscribeToResponses(GatewayResponseType.ExecutionReport, handler);
    this.gatewayService.subscribeToResponses(GatewayResponseType.OrderRejectedResponse, handler);
    this.gatewayService.subscribeToResponses(GatewayResponseType.ModifyOrCancelReject, handler);
  }

  protected getDefaultRequestParams = (): Partial<OrderWebSocketRequest> => ({
    traceIdentifier: uuidv4(),
  });

  public handleResponseEvent = (event: WebSocketEventData<any>): void => {
    const data = event.data ?? {};
    configLib.store.dispatch(sorOrderEvent(data));
  };

  protected sendRequestToGw(request: OrderWebSocketRequest | FXOrderRequest, isOnline = true): boolean {
    if (!this.gatewayService.isConnected() || !isOnline) {
      return false;
    }

    this.orderRequests.push(request);
    this.gatewayService.sendRequest(request);
    return true;
  }

  public sendNewOrder(order: Order, isOnline = true): void {
    const requestData: SOROrderRequestDataBase = setSOROrderData(order);
    const request = {
      ...this.getDefaultRequestParams(),
      ...requestData,
      messageType: GatewayRequestType.NewOrderRequest,
      dateTime: new Date().toISOString(),
      capacity: 'Agency',
      status: 'New',
    } as OrderWebSocketRequest;

    if (!this.sendRequestToGw(request, isOnline)) {
      logger.warn(`${LOG_PREFIX} new order failed! isOnline:${isOnline}, request:`, request);
      configLib.store.dispatch(newOrderFailed());
      return;
    }
    OrderInfoCache.push(request);
  }

  public sendModifyOrder(data: ModifyOrCancelOrderRequest, isOnline = true): void {
    const requestData = {
      ...setSOROrderData(data),
      origClOrdId: data.originalClientOrderId,
    };

    const request = {
      ...this.getDefaultRequestParams(),
      ...requestData,
      messageType: GatewayRequestType.ModifyOrderRequest,
    } as OrderWebSocketRequest;

    if (!this.sendRequestToGw(request, isOnline)) {
      logger.warn(`${LOG_PREFIX} modify order failed! isOnline:${isOnline}, request:`, request);
      configLib.store.dispatch(modifyOrderFailed());
      return;
    }
    OrderInfoCache.push(request);
  }

  public sendCancelOrder(data: ModifyOrCancelOrderRequest, isOnline = true): void {
    const { clientOrderId, originalClientOrderId, ...rest } = data as ModifyOrCancelOrderRequest & Order;
    const request = {
      ...this.getDefaultRequestParams(),
      ...rest,
      clOrdId: clientOrderId,
      origClOrdId: originalClientOrderId,
      messageType: GatewayRequestType.CancelOrderRequest,
    } as OrderWebSocketRequest;

    if (!this.sendRequestToGw(request, isOnline)) {
      logger.warn(`${LOG_PREFIX} cancel order failed! isOnline:${isOnline}, request:`, request);
      configLib.store.dispatch(cancelOrderFailed({}));
      return;
    }
    OrderInfoCache.push(request);
  }

  public sendFxOrder(request: FXOrderRequest, isOnline = true): void {
    const requestWithDefaults = {
      ...this.getDefaultRequestParams(),
      ...request,
      messageType: GatewayRequestType.NewOrderRequest,
      dateTime: request.dateTime ?? new Date().toISOString(),
      capacity: request.capacity ?? 'Agency',
    } as FXOrderRequest;

    if (!this.sendRequestToGw(requestWithDefaults, isOnline)) {
      logger.warn(`${LOG_PREFIX} Fx order failed! isOnline:${isOnline}, request:`, request);
      configLib.store.dispatch(newFXOrderFailed());
      return;
    }
    OrderInfoCache.push(requestWithDefaults);
  }
}
